import { z } from "zod";

/**
 * How a command invocation ended. Written once so the App controller, the
 * developer inspector and the assistant's live-state readback describe the
 * same facts with the same words.
 */

/** A failure that stopped the write: the transport, the server call, or the tool. */
export const AppInvocationFailureSchema = z.enum([
  "transport",
  "command",
  "tool",
]);
export type AppInvocationFailure = z.infer<typeof AppInvocationFailureSchema>;

/** Why an invocation never reached the transport, or stopped being deliverable. */
export const AppInvocationRejectionSchema = z.enum([
  "busy",
  "invalid",
  "disposed",
  "unknown-command",
]);
export type AppInvocationRejection = z.infer<
  typeof AppInvocationRejectionSchema
>;

/** Why a live App stopped delivering to a consumer it still had work for. */
export const AppInvocationCancellationSchema = z.enum([
  "owner-retired",
  "app-disposed",
]);
export type AppInvocationCancellation = z.infer<
  typeof AppInvocationCancellationSchema
>;

/**
 * The one terminal fact a consumer acts on. `succeeded` with refresh warnings
 * is still success: the write applied and only some feeds failed to reload.
 */
export const AppInvocationResultSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("succeeded"),
    /** Feed keys whose reload failed after the write applied. */
    refreshWarnings: z.array(z.string()).readonly(),
  }),
  z.object({
    kind: z.literal("failed"),
    failure: AppInvocationFailureSchema,
    message: z.string(),
  }),
  z.object({
    kind: z.literal("rejected"),
    reason: AppInvocationRejectionSchema,
    message: z.string(),
  }),
  z.object({
    kind: z.literal("canceled"),
    reason: AppInvocationCancellationSchema,
    /** Cancellation is not permission to overlap writes: the server may still finish. */
    remoteMayComplete: z.boolean(),
  }),
  z.object({
    kind: z.literal("ignored"),
    reason: z.literal("late-result"),
  }),
]);
export type AppInvocationResult = z.infer<typeof AppInvocationResultSchema>;

/** The phases an invocation passes through, in order. */
export const AppInvocationPhaseSchema = z.enum([
  "requested",
  "started",
  "rejected",
  "settled",
  "canceled",
  "ignored",
]);
export type AppInvocationPhase = z.infer<typeof AppInvocationPhaseSchema>;
